import * as React from "react";

export type BerichtAction = "addOpmerking" | "opnieuw" | "afhandelen";

interface IDialogHandlers {
  onClose: (result: boolean) => void;
}

interface IDialogContextProps {
  open: (component: React.ReactNode, options: IDialogOptions) => void;
  close: (result: boolean) => void;
  setHandlers: (handlers: IDialogHandlers) => void;
  isOpen: boolean;
  component: React.ReactNode | null;
  options: IDialogOptions | null;
}

interface IDialogOptions {
  title: string;
}

const DialogContext = React.createContext<IDialogContextProps>({
  open: () => {},
  close: () => {},
  setHandlers: () => {},
  isOpen: false,
  component: null,
  options: null,
});

interface IDialogContextProviderProps {
  children: React.ReactNode;
}

export const DialogContextProvider: React.FC<IDialogContextProviderProps> = ({
  children,
}) => {
  const [component, setComponent] = React.useState<React.ReactNode | null>(
    null
  );
  const [handlers, setHandlers] = React.useState<IDialogHandlers | null>(null);
  const [options, setOptions] = React.useState<IDialogOptions | null>(null);

  const handleOpen = React.useCallback(
    (component, options) => {
      setOptions(options);
      setComponent(component);
    },
    [setOptions, setComponent]
  );

  const handleClose = React.useCallback(
    (result: boolean) => {
      setComponent(null);

      if (handlers?.onClose) {
        handlers.onClose(result);
      }
    },
    [setComponent, handlers]
  );

  const handleSetHandlers = React.useCallback(
    (handlers: IDialogHandlers) => {
      setHandlers(handlers);
    },
    [setHandlers]
  );

  return (
    <DialogContext.Provider
      value={{
        open: handleOpen,
        close: handleClose,
        isOpen: component !== null,
        component,
        options,
        setHandlers: handleSetHandlers,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
};

export const useDialogContext = (): IDialogContextProps => {
  return React.useContext(DialogContext);
};
